import React from 'react';
import {
    Bar,
    Line,
    Pie,
    Doughnut
} from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { formatDate2, formatDate3, formatDate4 } from '../../utils/utils';
import { Card } from "@tremor/react";
import IconSelector from '../IconSelector/IconSelector';
import { usePanel } from '../PanelContext/PanelContext';
import InfoTooltip from '../InfoToolTip';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
);

const COLORS = ['rgb(140, 214, 16)', 'rgb(239, 198, 0)', 'rgb(231, 24, 49)'];

function index(perc) {
    return perc < 50 ? 0 : perc < 90 ? 1 : 2;
}

const SingleGauge = ({ data, icon }) => {
    const values = data.map(item => parseFloat(item.result) || 0);
    const average = values.reduce((a, b) => a + b, 0) / values.length;
    const percentage = Math.min(Math.max(average, 0), 100);
    const displayValue = percentage.toFixed(2);

    const gaugeData = {
        datasets: [{
            data: [percentage, 100 - percentage],
            backgroundColor: (ctx) => {
                if (ctx.type !== 'data') return;
                if (ctx.index === 1) return 'rgba(234, 234, 234, 0.5)';
                return COLORS[index(ctx.raw)];
            },
            borderWidth: 0
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        circumference: 180,
        rotation: -90,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        return `${context.raw.toFixed(2)}%`;
                    }
                }
            }
        }
    };

    return (
        <div className='custom-chart-container' style={{ position: 'relative', width: '100%', height: '100%' }}>
            <Doughnut data={gaugeData} options={options} />
            <div style={{
                position: 'absolute',
                top: '60%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                width: '100%'
            }}>
                <div style={{
                    fontSize: '36px',
                    fontWeight: 'bold',
                    color: COLORS[index(percentage)],
                }}>
                    {icon && (
                        <div className='w-full flex justify-center mt-5'>
                            <div
                                className="w-10 h-10 rounded-full flex justify-center items-center bg-turquesa"
                                style={{
                                    color: '#ffffff',
                                }}
                            >
                                <IconSelector
                                    value={icon}
                                    className="w-5 h-5"
                                />
                            </div>
                        </div>
                    )}
                    {displayValue}%
                </div>
            </div>
        </div>
    );
};

const SingleChart = ({
    data = [],
    chartType = 'bar',
    title = '',
    icon = '',
    indexKey = "date_response",
    valueKey = "result",
    categoryKey = "term",
    firstItem = null,
    colors = [
        '#3B82F6',
        '#10B981',
        '#F59E0B',
        '#EF4444',
        '#8B5CF6',
        '#EC4899',
        '#14B8A6',
        '#F97316',
        '#6366F1',
        '#06B6D4',
        '#84CC16',
        '#D946EF',
        '#22C55E',
        '#F43F5E',
        '#0EA5E9',
        '#A855F7'
    ],
    showLegend = false,
    valueFormatter = (v) => Intl.NumberFormat("us").format(v),
    className = "h-80",
}) => {
    if (!data || data.length === 0) return null;

    let labels;
    let values;
    let units = [];

    if (data[0].chart == 2 || data[0].chart == 4) {
        labels = data.map(item => formatDate3(item[indexKey]));
        values = data.map(item => parseFloat(item[valueKey]) || 0);
        units = data.map(item => item.unit || '');
    } else {
        const formattedData = data.map((item) => ({
            ...item,
            [indexKey]: formatDate3(item[indexKey]),
            [categoryKey]: item[categoryKey]?.replace(/_/g, ' '),
            value: Number(item[valueKey]) || 0,
            unit: item.unit || ''
        }));

        labels = formattedData.map(d => d[indexKey]);
        values = formattedData.map(d => d.value);
        units = formattedData.map(d => d.unit);
    }

    const chartData = {
        labels,
        datasets: [
            {
                label: title,
                data: values,
                backgroundColor: colors,
                borderColor: colors,
                borderWidth: 1,
                fill: chartType === 'area',
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                labels: {
                    color: 'rgb(0, 0, 0)',
                    padding: 20,
                    font: {
                        size: 12,
                        family: "'Arial', sans-serif"
                    },
                },
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        const unit = units[tooltipItem.dataIndex] ? ` ${units[tooltipItem.dataIndex]}` : '';
                        return `${chartData.labels[tooltipItem.dataIndex]}: ${valueFormatter(tooltipItem.raw)}${unit}`;
                    }
                },
                bodyFont: {
                    size: 12,
                    family: "'Arial', sans-serif"
                }
            }
        }
    };

    const chartProps = {
        data: chartData,
        options,
    };

    let ChartComponent;
    switch (data[0].chart) {
        case 2:
            ChartComponent = Pie;
            break;
        case 3:
            ChartComponent = Line;
            break;
        case 4:
            ChartComponent = Doughnut;
            break;
        case 5:
            return (
                <div className='custom-chart-container'>
                    <Card
                        className={`${className} relative transition-all shadow-md hover:shadow-lg border-2 rounded-lg overflow-hidden`}
                        style={{
                            borderColor: firstItem?.color_card || '#3B82F6',
                            background: `linear-gradient(135deg, ${firstItem?.color_card}15 0%, #ffffff 100%)`,
                        }}
                    >
                        <div className="p-4">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-3 first-letter:uppercase">
                                {title}
                            </h2>
                            <div className="relative h-48">
                                <SingleGauge data={data} icon={icon} />
                            </div>
                        </div>
                    </Card>
                </div>
            );
        case 6:
            ChartComponent = Bar;
            options.indexAxis = 'y';
            break;
        default:
            ChartComponent = Bar;
            break;
    }

    return (
        <div className=''>
            <Card
                className={`${className} relative transition-all shadow-md hover:shadow-lg border-2 rounded-lg overflow-hidden`}
                style={{
                    borderColor: firstItem?.color_card || '#3B82F6',
                    background: `linear-gradient(135deg, ${firstItem?.color_card}15 0%, #ffffff 100%)`,
                }}
            >
                <div className="p-4">
                    <h2
                        className="text-lg font-bold mb-4 flex items-center gap-3"
                    >
                        {title}
                    </h2>
                    <div className={"relative h-48"}>
                        <ChartComponent {...chartProps} />
                    </div>
                </div>
            </Card>
        </div>
    );
};

const ChartComponent = ({ rawData, chartType = 'bar' }) => {
    const { isPanelVisible } = usePanel();

    if (!rawData || typeof rawData !== 'object') {
        return <p className="text-center py-8">No hay datos disponibles</p>;
    }

    const isGroupedByTerm = Array.isArray(Object.values(rawData)[0]);

    const transformData = (groupKey, groupData) => {
        const result = [];

        for (const [date, entries] of Object.entries(groupData)) {
            entries.forEach(entry => {
                result.push({
                    ...entry,
                    date_response: formatDate2(date),
                    term: groupKey
                });
            });
        }

        return result;
    };

    const groupedEntries = Object.entries(rawData);

    return (
        <div className={`grid ${isPanelVisible ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
            {groupedEntries.map(([groupKey, groupData]) => {
                const data = isGroupedByTerm
                    ? groupData.map((entry) => ({
                        ...entry,
                        date_response: formatDate4(entry.date_response),
                    }))
                    : transformData(groupKey, groupData);

                const firstItem = Array.isArray(groupData)
                    ? groupData[0]
                    : Object.values(groupData)[0]?.[0];

                return (
                    <div className='w-full custom-chart-container' key={groupKey}>
                        <div className="h-full flex flex-col bg-white rounded-lg shadow-md hover:shadow-lg transition-all">
                            {/* Encabezado con altura fija */}
                            <div className={`p-4 ${isPanelVisible ? 'flex items-center' : 'flex flex-col items-center'} gap-3 min-h-[80px] border-b`}>
                                {(firstItem?.icon_indicator || firstItem?.icon) && (
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                                        style={{
                                            backgroundColor: firstItem.color_card || '#3B82F6',
                                            color: firstItem.color_text || '#ffffff',
                                            boxShadow: `0 4px 12px ${firstItem.color_card}55`,
                                        }}>
                                        <IconSelector
                                            value={firstItem?.icon_indicator || firstItem?.icon}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                )}
                                <h3 className={`font-semibold text-lg md:text-center first-letter:uppercase ${isPanelVisible ? '' : 'line-clamp-2'}`}>
                                    {firstItem?.indicator || groupKey.replace(/_/g, ' ')} {firstItem?.description && <InfoTooltip text={firstItem?.description} />}
                                </h3>
                            </div>

                            {/* Contenedor del gráfico con altura flexible */}
                            <div className="flex-1 min-h-0 p-4">
                                <SingleChart
                                    data={data}
                                    chartType={chartType}
                                    className="h-full"
                                    icon={firstItem?.icon_indicator || firstItem?.icon}
                                    firstItem={firstItem}
                                    showLegend={true}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ChartComponent;